import { useEffect, useState } from "react";
import api, { fmtMXN, todayLocal } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Paperclip } from "lucide-react";
import { Modal, In, Sel } from "@/pages/Projects";

const TYPES = [
  { value: "volumenes_adicionales", label: "Volúmenes adicionales" },
  { value: "conceptos_extraordinarios", label: "Conceptos extraordinarios" },
  { value: "ampliacion_monto", label: "Ampliación de monto" },
  { value: "ampliacion_plazo", label: "Ampliación de plazo" },
];

export default function AmendmentsTab({ projectId, budgetItems, canManage, user }) {
  const [amendments, setAmendments] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const emptyItem = { budget_item_id: "", code: "", description: "", unit: "", unit_price: "", qty_additional: "" };
  const [form, setForm] = useState({ type: "volumenes_adicionales", reference: "", date: todayLocal(), description: "", new_end_date: "", items: [] });

  const load = async () => {
    const { data } = await api.get(`/projects/${projectId}/amendments`);
    setAmendments(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const startNew = () => {
    setForm({ type: "volumenes_adicionales", reference: "", date: todayLocal(), description: "", new_end_date: "", items: [{ ...emptyItem, budget_item_id: budgetItems[0]?.id || "" }] });
    setFiles([]);
    setOpenNew(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const documents = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        documents.push(data.id);
      }
      const withItems = ["volumenes_adicionales", "conceptos_extraordinarios"].includes(form.type);
      await api.post(`/projects/${projectId}/amendments`, {
        type: form.type, reference: form.reference, date: form.date || null,
        description: form.description || null, new_end_date: form.new_end_date || null,
        documents,
        items: withItems ? form.items.filter((i) => Number(i.qty_additional) > 0).map((i) => ({
          budget_item_id: form.type === "volumenes_adicionales" ? i.budget_item_id || null : null,
          code: i.code || null, description: i.description || "(concepto)", unit: i.unit || null,
          unit_price: Number(i.unit_price || 0), qty_additional: Number(i.qty_additional || 0),
        })) : [],
      });
      toast.success("Convenio registrado");
      setOpenNew(false); load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Error"); }
    setSaving(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar convenio ${a.folio}?`)) return;
    try { await api.delete(`/amendments/${a.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const withItems = ["volumenes_adicionales", "conceptos_extraordinarios"].includes(form.type);

  return (
    <div className="space-y-3" data-testid="amendments-tab">
      <div className="flex justify-between items-center">
        <span className="label-mono" style={{ color: "var(--cyan)" }}>// CONVENIOS MODIFICATORIOS · autorizan volúmenes adicionales y extraordinarios sin tocar el catálogo original</span>
        {canManage && <button data-testid="new-amendment-btn" onClick={startNew} className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"><Plus className="w-3 h-3" /> Convenio</button>}
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="amendments-table">
          <thead className="panel-header"><tr className="text-left">
            <th className="label-mono px-4 py-3">Folio</th><th className="label-mono px-4 py-3">Tipo</th>
            <th className="label-mono px-4 py-3">Referencia</th><th className="label-mono px-4 py-3">Conceptos</th>
            <th className="label-mono px-4 py-3 text-right">Monto adicional</th><th className="label-mono px-4 py-3">Registró</th>
            <th className="label-mono px-4 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {amendments.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500">Sin convenios registrados.</td></tr>}
            {amendments.map((a) => (
              <tr key={a.id} className="row-hover align-top" style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="px-4 py-3 font-mono text-xs font-bold" style={{ color: "var(--cyan)" }}>{a.folio}</td>
                <td className="px-4 py-3 text-xs uppercase text-slate-300">{TYPES.find((t) => t.value === a.type)?.label || a.type}</td>
                <td className="px-4 py-3 text-xs text-slate-200">{a.reference}<div className="text-[10px] text-slate-500">{a.date}</div></td>
                <td className="px-4 py-3 text-xs text-slate-300">
                  {(a.items || []).map((it, i) => <div key={i}>{it.code ? `${it.code} · ` : ""}{it.description?.slice(0, 60)}: <span className="font-mono">+{it.qty_additional} {it.unit || ""}</span></div>)}
                  {a.new_end_date && <div>Nueva fecha término: <span className="font-mono">{a.new_end_date}</span></div>}
                  {a.documents?.length > 0 && <div className="text-[10px] text-slate-500 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {a.documents.length} documento(s)</div>}
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs">{fmtMXN(a.amount_additional)}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{a.created_by_name}</td>
                <td className="px-4 py-3 text-right">
                  {user?.role === "admin" && <button data-testid={`delete-amendment-${a.id}`} onClick={() => remove(a)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openNew && (
        <Modal onClose={() => setOpenNew(false)} title="Registrar convenio modificatorio">
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Tipo" testId="am-type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TYPES} />
              <In label="Referencia (No. convenio/oficio)" testId="am-reference" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Fecha" testId="am-date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required={false} />
              {form.type === "ampliacion_plazo" && <In label="Nueva fecha de término" testId="am-end-date" type="date" value={form.new_end_date} onChange={(v) => setForm({ ...form, new_end_date: v })} required={false} />}
            </div>
            <In label="Descripción" testId="am-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} required={false} />
            {withItems && (
              <div className="space-y-2">
                <span className="label-mono">Volúmenes autorizados</span>
                {form.items.map((it, idx) => (
                  <div key={idx} className="space-y-1.5 p-2 rounded-sm" style={{ border: "1px solid var(--border)" }}>
                    {form.type === "volumenes_adicionales" ? (
                      <Sel label="Concepto del catálogo" testId={`am-bi-${idx}`} value={it.budget_item_id} onChange={(v) => upItem(idx, "budget_item_id", v)} options={budgetItems.map((b) => ({ value: b.id, label: `${b.code || ""} ${b.description?.slice(0, 80)}` }))} />
                    ) : (
                      <div className="grid grid-cols-[1fr_2fr_1fr] gap-2">
                        <In label="Clave" testId={`am-code-${idx}`} value={it.code} onChange={(v) => upItem(idx, "code", v)} required={false} />
                        <In label="Descripción" testId={`am-desc-${idx}`} value={it.description} onChange={(v) => upItem(idx, "description", v)} />
                        <In label="Unidad" testId={`am-unit-${idx}`} value={it.unit} onChange={(v) => upItem(idx, "unit", v)} required={false} />
                      </div>
                    )}
                    <div className="grid grid-cols-[1fr_1fr_32px] gap-2 items-end">
                      <In label="Cantidad adicional" testId={`am-qty-${idx}`} type="number" value={it.qty_additional} onChange={(v) => upItem(idx, "qty_additional", v)} />
                      <In label={form.type === "volumenes_adicionales" ? "P.U. (vacío = del catálogo)" : "P.U. autorizado"} testId={`am-pu-${idx}`} type="number" value={it.unit_price} onChange={(v) => upItem(idx, "unit_price", v)} required={false} />
                      <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="p-2 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                <button type="button" data-testid="am-add-item" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem, budget_item_id: budgetItems[0]?.id || "" }] })} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Concepto</button>
              </div>
            )}
            <div>
              <label className="label-mono block mb-1.5">Documentos (convenio firmado, oficios)</label>
              <input data-testid="am-documents" type="file" multiple onChange={(e) => setFiles([...e.target.files])} className="text-xs text-slate-400" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenNew(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="save-amendment-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Registrar convenio</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );

  function upItem(idx, key, val) {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: val };
    setForm({ ...form, items });
  }
}
